import { MAP } from '../utils/constants';

/** Cached grid road centerline coordinates (same layout as World.ts grid strips). */
function roadCenters(): { cx: number[]; cz: number[] } {
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH, SIZE } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = SIZE - totalRoadSpace;
  const blockW = inner / GRID;

  const line: number[] = [];
  for (let i = 0; i <= GRID; i++) {
    line.push(-HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2);
  }
  return { cx: line, cz: line };
}

let cachedCenters: { cx: number[]; cz: number[] } | null = null;

function centers(): { cx: number[]; cz: number[] } {
  if (!cachedCenters) cachedCenters = roadCenters();
  return cachedCenters;
}

const HALF_ROAD = MAP.ROAD_WIDTH * 0.5;
const ROAD_MARGIN = 8;

/**
 * True if (x,z) lies on or near the union of grid asphalt strips.
 */
export function isPointOnGridRoad(x: number, z: number): boolean {
  const { cx, cz } = centers();
  for (const xc of cx) {
    if (Math.abs(x - xc) <= HALF_ROAD + ROAD_MARGIN) return true;
  }
  for (const zc of cz) {
    if (Math.abs(z - zc) <= HALF_ROAD + ROAD_MARGIN) return true;
  }
  return false;
}

/**
 * Closest point on the grid road network (vertical strips ∪ horizontal strips ∪ intersections).
 */
export function snapPointToGridRoad(x: number, z: number): { x: number; z: number } {
  if (isPointOnGridRoad(x, z)) {
    return { x, z };
  }

  const { cx, cz } = centers();
  let bestX = x;
  let bestZ = z;
  let bestD2 = Infinity;

  for (const xc of cx) {
    const px = xc;
    const pz = z;
    const d2 = (x - px) ** 2 + (z - pz) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestX = px;
      bestZ = pz;
    }
  }

  for (const zc of cz) {
    const px = x;
    const pz = zc;
    const d2 = (x - px) ** 2 + (z - pz) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestX = px;
      bestZ = pz;
    }
  }

  for (const xc of cx) {
    for (const zc of cz) {
      const px = xc;
      const pz = zc;
      const d2 = (x - px) ** 2 + (z - pz) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestX = px;
        bestZ = pz;
      }
    }
  }

  const lim = MAP.HALF - 8;
  return {
    x: Math.max(-lim, Math.min(lim, bestX)),
    z: Math.max(-lim, Math.min(lim, bestZ)),
  };
}

/** Max distance we allow a snap to move the cop onto the grid. */
const MAX_SNAP_DIST = 80;

/**
 * Among grid road points within `maxSnap` of (x,z), pick the one **closest to the chase target**
 * (usually the player). Stops cops snapping onto a parallel road away from you.
 */
export function snapPointToGridRoadTowardGoal(
  x: number,
  z: number,
  goalX: number,
  goalZ: number,
): { x: number; z: number } {
  if (isPointOnGridRoad(x, z)) {
    return { x, z };
  }

  const { cx, cz } = centers();
  const lim = MAP.HALF - 8;
  const maxSnap2 = MAX_SNAP_DIST * MAX_SNAP_DIST;

  const candidates: Array<{ px: number; pz: number }> = [];
  for (const xc of cx) {
    candidates.push({ px: xc, pz: z });
  }
  for (const zc of cz) {
    candidates.push({ px: x, pz: zc });
  }
  for (const xc of cx) {
    for (const zc of cz) {
      candidates.push({ px: xc, pz: zc });
    }
  }

  let best: { px: number; pz: number } | null = null;
  let bestGoalD2 = Infinity;
  let bestSnapD2 = Infinity;

  for (const c of candidates) {
    const px = Math.max(-lim, Math.min(lim, c.px));
    const pz = Math.max(-lim, Math.min(lim, c.pz));
    const snapD2 = (px - x) ** 2 + (pz - z) ** 2;
    if (snapD2 > maxSnap2) continue;

    const goalD2 = (goalX - px) ** 2 + (goalZ - pz) ** 2;
    if (
      best === null ||
      goalD2 < bestGoalD2 - 1e-4 ||
      (Math.abs(goalD2 - bestGoalD2) < 1e-4 && snapD2 < bestSnapD2)
    ) {
      best = { px, pz };
      bestGoalD2 = goalD2;
      bestSnapD2 = snapD2;
    }
  }

  if (best) {
    return { x: best.px, z: best.pz };
  }

  return snapPointToGridRoad(x, z);
}

/**
 * VERY LIGHT road constraint for cops - only applies a gentle nudge, never kills velocity.
 * This allows cops to cut corners and take shortcuts while still preferring roads.
 */
export function constrainCopToGridRoad(
  x: number,
  z: number,
  vx: number,
  vz: number,
  _goalX: number,
  _goalZ: number,
): { x: number; z: number; vx: number; vz: number } {
  if (isPointOnGridRoad(x, z)) {
    return { x, z, vx, vz };
  }

  return { x, z, vx, vz };
}
